import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { useSiteContent } from "@/lib/content";

const inputClass =
  "w-full bg-surface border border-line px-4 py-3.5 text-sm text-bone placeholder:text-fog/50 focus:border-gold focus:outline-none transition-colors duration-300";

const EMPTY_FORM = { name: "", email: "", company: "", project_type: "Product / Still Life", message: "" };

const Contact = () => {
  const content = useSiteContent();
  const tx = (key, fallback) => content?.texts?.[key] || fallback;
  const [form, setForm] = useState(EMPTY_FORM);
  const [privacy, setPrivacy] = useState(false);
  const [sending, setSending] = useState(false);

  const update = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!privacy) {
      toast.error("Per inviare il messaggio devi accettare la Privacy Policy.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", { ...form, privacy });
      toast.success("Messaggio inviato. Ti rispondo entro 24 ore lavorative.");
      setForm(EMPTY_FORM);
      setPrivacy(false);
    } catch {
      toast.error("Invio non riuscito. Riprova o scrivi a verardonicola@gmail.com");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page" className="bg-night pt-32 min-h-screen">
      <Seo
        title="Contatti — Nicola Verardo Visual Production"
        description="Raccontami prodotto o spazio, obiettivi e tempistiche: ti rispondo personalmente entro 24 ore lavorative con una prima valutazione concreta."
        path="/contact"
      />
      <div className="px-6 sm:px-12 lg:px-20 pb-24 grid lg:grid-cols-2 gap-16">
        <Reveal>
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-fog">{tx("contact_label", "Contatti")}</p>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-normal tracking-tight text-bone">
            {tx("contact_title", "Parliamo del progetto.")}
          </h1>
          <p className="mt-6 text-base text-fog leading-relaxed max-w-md">
            {tx("contact_intro", "Raccontami prodotto o spazio, obiettivi e tempistiche. Ti rispondo personalmente entro 24 ore lavorative, con una prima valutazione concreta.")}
          </p>
          <div className="mt-12 space-y-5">
            <a href="mailto:verardonicola@gmail.com" data-testid="contact-email-link" className="flex items-center gap-4 text-sm text-bone hover:text-gold transition-colors duration-300">
              <Mail size={16} className="text-gold" /> verardonicola@gmail.com
            </a>
            <a href="tel:+393933322814" data-testid="contact-phone-link" className="flex items-center gap-4 text-sm text-bone hover:text-gold transition-colors duration-300">
              <Phone size={16} className="text-gold" /> +39 393 3322814
            </a>
          </div>
          <div className="mt-10 flex items-center gap-5">
            <a data-testid="contact-social-instagram" href="https://www.instagram.com/nicola.verardo.still_life/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-fog hover:text-gold transition-colors duration-300">
              <Instagram size={20} />
            </a>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <form data-testid="contact-form" onSubmit={handleSubmit} className="space-y-5 lg:pt-24">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="contact-name" className="block text-[10px] font-mono tracking-[0.25em] text-fog uppercase mb-2">Nome *</label>
                <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={update("name")} placeholder="Il tuo nome" className={inputClass} />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-[10px] font-mono tracking-[0.25em] text-fog uppercase mb-2">Email *</label>
                <input id="contact-email" data-testid="contact-email-input" required type="email" value={form.email} onChange={update("email")} placeholder="nome@azienda.com" className={inputClass} />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="contact-company" className="block text-[10px] font-mono tracking-[0.25em] text-fog uppercase mb-2">Azienda</label>
                <input id="contact-company" data-testid="contact-company-input" value={form.company} onChange={update("company")} placeholder="Brand o studio" className={inputClass} />
              </div>
              <div>
                <label htmlFor="contact-type" className="block text-[10px] font-mono tracking-[0.25em] text-fog uppercase mb-2">Tipo di progetto</label>
                <select id="contact-type" data-testid="contact-type-select" value={form.project_type} onChange={update("project_type")} className={inputClass}>
                  <option>Product / Still Life</option>
                  <option>Architecture / Interiors</option>
                  <option>Motion / Video</option>
                  <option>AI &amp; Visual Production</option>
                  <option>Altro</option>
                </select>
              </div>
            </div>
            <div>
              <label htmlFor="contact-message" className="block text-[10px] font-mono tracking-[0.25em] text-fog uppercase mb-2">Messaggio *</label>
              <textarea id="contact-message" data-testid="contact-message-input" required rows={6} value={form.message} onChange={update("message")} placeholder="Descrivi il progetto: prodotto o spazio, obiettivi, tempistiche…" className={`${inputClass} resize-none`} />
            </div>
            <label data-testid="contact-privacy-label" className="flex items-start gap-3 cursor-pointer select-none">
              <input
                id="contact-privacy"
                type="checkbox"
                required
                data-testid="contact-privacy-checkbox"
                checked={privacy}
                onChange={(e) => setPrivacy(e.target.checked)}
                className="mt-0.5 w-4 h-4 shrink-0 accent-[#C6A15B] cursor-pointer"
              />
              <span className="text-xs text-fog leading-relaxed">
                Ho letto la <Link to="/privacy" data-testid="contact-privacy-link" className="text-gold underline underline-offset-2 hover:text-bone transition-colors duration-300">Privacy Policy</Link> e acconsento al trattamento dei miei dati personali per essere ricontattato. *
              </span>
            </label>
            <button
              type="submit"
              data-testid="contact-form-submit-button"
              disabled={sending}
              className="w-full sm:w-auto px-10 py-4 bg-gold text-night text-xs font-semibold tracking-[0.2em] hover:bg-bone disabled:opacity-60 transition-colors duration-300"
            >
              {sending ? "INVIO IN CORSO…" : "INVIA IL MESSAGGIO"}
            </button>
            <p className="text-[10px] font-mono tracking-[0.15em] text-fog/60">{tx("contact_promise", "RISPOSTA ENTRO 24 ORE LAVORATIVE — NESSUNA NEWSLETTER, NESSUNO SPAM")}</p>
          </form>
        </Reveal>
      </div>
    </main>
  );
};

export default Contact;
